import {makeAutoObservable, reaction} from 'mobx';
import {screenSizeProvider} from '@stores/ScreenSizeProvider';
import {kbPages, PAGES, settingsPages} from '@stores/ViewStore';
import {RootStoreModel} from "@stores/RootStore";

export type MobileLayer = 'mainSidebar' | 'kbTree' | 'settingsSecondary' | 'rightMenu' | 'page';

/**
 * MobileNavController
 *
 * Lightweight controller to unify mobile navigation behavior across:
 *  - MainSidebar (primary navigation)
 *  - KB TreeLayout (secondary navigation for KB)
 *  - Page content
 *
 * Not MST; simple MobX for responsiveness and minimal overhead.
 */
export class MobileNavController {
    layer: MobileLayer = 'page';
    rootStore: RootStoreModel;
    _prevPageName?: string;

    constructor(rootStore: RootStoreModel) {
        this.rootStore = rootStore;
        makeAutoObservable(this, {}, {autoBind: true});

        // Keep the layer consistent with current page + screen size + tree collapsed state
        reaction(
            () => ({
                view: screenSizeProvider.view,
                page: this.rootStore.view.page,
                treeCollapsed: this.rootStore.isTreeCollapsed,
                rightMenu: this.rootStore.rightMenu,
            }),
            ({page}) => this.ensureLayerForCurrentContext(page),
            {fireImmediately: true}
        );
    }

    get isMobile() {
        return screenSizeProvider.view !== 'desktop';
    }

    get showMainSidebar() {
        return this.isMobile && this.layer === 'mainSidebar';
    }

    get showKbTree() {
        return this.isMobile && this.layer === 'kbTree';
    }

    get showPage() {
        // Always show content on desktop; on mobile only when in page layer
        return !this.isMobile || this.layer === 'page';
    }

    get showSettingsSecondary() {
        return this.isMobile && this.layer === 'settingsSecondary';
    }

    get showRightMenu() {
        return this.isMobile && this.layer === 'rightMenu';
    }

    openMainSidebar() {
        if (!this.isMobile) return; // No-op on desktop
        this.layer = 'mainSidebar';
        const root = this.rootStore;
        root.toggleIsSidebarCollapsed(false)
    }

    openKbTree() {
        const root = this.rootStore;
        if (!this.isMobile) return; // No-op on desktop
        // Ensure KB tree is visible
        if (root.isTreeCollapsed) root.toggleIsTreeCollapsed(false);
        this.layer = 'kbTree';
    }

    openPage() {
        const root = this.rootStore;
        if (this.isMobile && kbPages.includes(root.view.page as any)) {
            // For KB pages make sure tree is collapsed so page is visible
            if (!root.isTreeCollapsed) root.toggleIsTreeCollapsed(true);
        }
        this.layer = 'page';
    }

    openSettingsSecondary() {
        if (!this.isMobile) return; // No-op on desktop
        this.layer = 'settingsSecondary';
    }

    openRightMenu() {
        if (!this.isMobile) return; // No-op on desktop
        const root = this.rootStore;
        if (kbPages.includes(root.view.page as any) && !root.isTreeCollapsed) {
            root.toggleIsTreeCollapsed(true);
        }
        this.layer = 'rightMenu';
    }

    /**
     * Derive the appropriate layer from current context.
     * Desktop → always page. Mobile → depends on kbPages and treeCollapsed.
     */
    ensureLayerForCurrentContext(pageName: string) {
        const root = this.rootStore;
        if (!this.isMobile) {
            this.layer = 'page';
            return;
        }
        if (this._prevPageName !== pageName) {
            this._prevPageName = pageName;
            if (pageName === PAGES.SETTINGS_GENERAL) {
                this.openSettingsSecondary();
                return;
            } else if (kbPages.includes(pageName as any)) {
                this.openKbTree();
                return;
            } else {
                this.openPage();
                return;
            }
        } else {
            if (root.rightMenu) {
                this.layer = 'rightMenu';
                return;
            }
            const page = root.view.page;
            const isKb = kbPages.includes(page as any);
            if (isKb) {
                this.layer = root.isTreeCollapsed ? 'page' : 'kbTree';
            } else {
                // For settings pages, default to page layer unless explicitly opened as a secondary layer.
                // This keeps automatic derivation simple and predictable.
                const isSettings = settingsPages.includes(page as any);
                if (isSettings && this.layer === 'settingsSecondary') {
                    // keep as-is if user intentionally opened secondary
                    return;
                }
                this.layer = 'page';
            }
        }
    }
}
